import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { ServiceOrderLineStatus } from '../types/service-order.types';
import { BaseEntity } from './base.entity';
import { ServiceOrderLineEntity } from './service-order-line.entity';
import { TripStopEntity } from './trip-stop.entity';

/**
 * PD-16797 — what happened to one service order line at one stop: the service
 * counterpart of `trip_stop_item`, which records each order line at a delivery
 * stop. The line itself holds only its latest outcome, so when a line not done on
 * one visit is finished on the next, the first stop's "not done, part on order"
 * would otherwise be overwritten. Written when the stop completes, one row per line
 * the stop recorded, and read back for that stop from then on (owner, 2026-10-08:
 * keep the outcome on the trip stop, not in a separate visits concept).
 *
 * SM1 exposure: none. New table; SM1 never names it.
 */
@Entity('trip_stop_service_line')
@Index('uq_trip_stop_service_line', ['tripStopId', 'serviceOrderLineId'], { unique: true })
@Index('idx_trip_stop_service_line_line', ['serviceOrderLineId'])
export class TripStopServiceLineEntity extends BaseEntity {
	@Column({ name: 'fk_trip_stop__id', type: 'int', unsigned: true })
	public tripStopId!: number;

	@Column({ name: 'fk_service_order_line__id', type: 'int', unsigned: true })
	public serviceOrderLineId!: number;

	/** `done` or `not_done`; a line still pending when the stop ends has no row. */
	@Column({ name: 'status', type: 'varchar', length: 16 })
	public status!: Exclude<ServiceOrderLineStatus, 'pending'>;

	@Column({ name: 'notDoneReason', type: 'varchar', length: 500, nullable: true })
	public notDoneReason!: string | null;

	@Column({ name: 'outcome', type: 'text', nullable: true })
	public outcome!: string | null;

	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes!: string | null;

	/** When the technician recorded the line at this stop. */
	@Column({ name: 'workedDate', type: 'datetime', nullable: true })
	public workedDate!: Date | null;

	@ManyToOne(() => TripStopEntity)
	@JoinColumn({ name: 'fk_trip_stop__id' })
	public fkTripStop?: TripStopEntity;

	@ManyToOne(() => ServiceOrderLineEntity)
	@JoinColumn({ name: 'fk_service_order_line__id' })
	public fkServiceOrderLine?: ServiceOrderLineEntity;
}
